import React, { useCallback, useMemo, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { debounce } from "lodash";
import { ADDRESS_LOOKUP_MIN_QUERY_LENGTH, MelissaApiAddress, useAddressLookup } from "./useAddressLookup";

const DEBOUNCE_MS = 300;

const AddressTypeahead: React.FC<{
    idPrefix: string;
    label: string;
    value: string | undefined;
    error?: string;
    required?: boolean;
    isMobile?: boolean;
    placeholder?: string;
    addressFieldsVisible?: boolean;
    onChange: (value: string) => void;
    onSelect: (address: MelissaApiAddress) => void;
    onManualEntry: () => void;
}> = ({ idPrefix, label, value, error, required, isMobile, placeholder, addressFieldsVisible, onChange, onSelect, onManualEntry }) => {
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const skipBlurRef = useRef(false);

    const inputId = `${idPrefix}-addr-lookup-input`;
    const inputName = `${idPrefix}-lookup-line1`;
    const listboxId = `${inputId}-listbox`;
    const errorId = `${inputId}-error`;
    const statusId = `${inputId}-status`;

    const handleResult = useCallback(
        (matches: MelissaApiAddress[]) => {
            if (matches.length === 0) {
                onManualEntry();
            }
        },
        [onManualEntry]
    );

    const [results, isLoading] = useAddressLookup(debouncedQuery, { onResult: handleResult });

    const hasQuery = debouncedQuery.trim().length >= ADDRESS_LOOKUP_MIN_QUERY_LENGTH;
    const showSuggestions = isOpen && hasQuery && (isLoading || results.length > 0);
    const noMatch = hasQuery && !isLoading && results.length === 0;

    const debouncedSetQuery = useMemo(() => debounce(setDebouncedQuery, DEBOUNCE_MS), []);

    const isHorizontal = !isMobile;
    const listboxRole: React.AriaRole = "listbox";
    const optionRole: React.AriaRole = "option";

    const handleInputChange = (text: string) => {
        onChange(text);
        debouncedSetQuery(text);
        setActiveIndex(-1);
        setIsOpen(true);
    };

    const selectSuggestion = (address: MelissaApiAddress) => {
        onSelect(address);
        debouncedSetQuery.cancel();
        setDebouncedQuery("");
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const handleManualEntry = () => {
        onManualEntry();
        setIsOpen(false);
    };

    const handleClear = () => {
        onChange("");
        debouncedSetQuery.cancel();
        setDebouncedQuery("");
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const handleListboxMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) {
            return;
        }
        skipBlurRef.current = true;
        window.addEventListener(
            "mouseup",
            () => {
                skipBlurRef.current = false;
                inputRef.current?.focus({ preventScroll: true });
            },
            { once: true }
        );
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            setIsOpen(true);
            return;
        }

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                setActiveIndex((index) => Math.min(index + 1, results.length - 1));
                break;
            case "ArrowUp":
                event.preventDefault();
                setActiveIndex((index) => Math.max(index - 1, 0));
                break;
            case "Enter":
                if (activeIndex >= 0 && results[activeIndex]) {
                    event.preventDefault();
                    selectSuggestion(results[activeIndex]);
                }
                break;
            case "Escape":
                setIsOpen(false);
                setActiveIndex(-1);
                break;
            default:
                break;
        }
    };

    return (
        <Form.Group className={cn({ "d-flex flex-column": !isHorizontal })}>
            <div className={cn({ "d-flex justify-content-between align-items-center": isHorizontal })}>
                <Form.Label htmlFor={inputId} style={{ width: "168px" }} className="text-truncate mb-1 d-block">
                    {label}
                    {required && <span aria-hidden="true">&thinsp;*</span>}
                </Form.Label>

                <div ref={containerRef} className={cn("position-relative", { "w-75": isHorizontal, "w-100": !isHorizontal })}>
                    <div className={cn("position-relative", { "is-invalid field-error": Boolean(error) })}>
                        <Form.Control
                            ref={inputRef}
                            id={inputId}
                            name={inputName}
                            className={cn("contact-input-field", { "pe-5": Boolean(value) })}
                            type="text"
                            role="combobox"
                            autoComplete="new-password"
                            autoCorrect="off"
                            spellCheck={false}
                            value={value ?? ""}
                            placeholder={placeholder}
                            isInvalid={Boolean(error)}
                            required={required}
                            aria-required={required}
                            aria-expanded={showSuggestions}
                            aria-controls={listboxId}
                            aria-autocomplete="list"
                            aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
                            aria-describedby={cn(error ? errorId : "", noMatch ? statusId : "") || undefined}
                            aria-invalid={Boolean(error)}
                            style={Boolean(value) ? { backgroundImage: "none" } : undefined}
                            onChange={(event) => handleInputChange(event.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={() => hasQuery && results.length > 0 && setIsOpen(true)}
                            onBlur={(event) => {
                                if (skipBlurRef.current) {
                                    return;
                                }
                                if (!containerRef.current?.contains(event.relatedTarget)) {
                                    setIsOpen(false);
                                }
                            }}
                        />
                        {Boolean(value) && (
                            <button
                                type="button"
                                className="position-absolute top-50 end-0 translate-middle-y border-0 p-0 me-3 bg-transparent text-gray-600"
                                style={{ zIndex: 2 }}
                                onClick={handleClear}
                                aria-label="Clear address"
                            >
                                <FontAwesomeIcon icon={"circle-xmark"} />
                            </button>
                        )}
                    </div>
                    {showSuggestions && (
                        <div
                            id={listboxId}
                            role={listboxRole}
                            aria-label="Address suggestions"
                            className="bg-white border rounded shadow-sm position-absolute w-100 mt-1 py-1 overflow-auto"
                            style={{ zIndex: 1000, maxHeight: "280px" }}
                            onMouseDown={handleListboxMouseDown}
                        >
                            {isLoading && (
                                <div className="d-flex align-items-center gap-2 px-3 py-2 text-gray-600">
                                    <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                                    <span>Searching addresses…</span>
                                </div>
                            )}
                            {!isLoading &&
                                results.map((address, index) => (
                                    <button
                                        key={`${address.addressLine1}-${address.postalCode}-${index}`}
                                        type="button"
                                        id={`${listboxId}-option-${index}`}
                                        role={optionRole}
                                        aria-selected={index === activeIndex}
                                        className={cn("btn d-flex align-items-center gap-2 w-100 text-start border-0 rounded-0 px-3 py-2", {
                                            "bg-light": index === activeIndex,
                                        })}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => selectSuggestion(address)}
                                    >
                                        <FontAwesomeIcon
                                            icon={["fal", "location-dot"]}
                                            className="text-gray-600 flex-shrink-0"
                                            aria-hidden="true"
                                        />
                                        <span>{highlightMatch(formatSuggestion(address), debouncedQuery)}</span>
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
            </div>
            {(noMatch || !addressFieldsVisible || error) && (
                <div className={cn({ "d-flex justify-content-between align-items-center": isHorizontal })}>
                    {isHorizontal && <div style={{ width: "168px" }} />}
                    <div className={cn({ "w-75": isHorizontal })}>
                        {error && (
                            <Form.Control.Feedback id={errorId} type="invalid" className="d-block">
                                {error}
                            </Form.Control.Feedback>
                        )}
                        {noMatch && (
                            <div id={statusId} role="status" aria-live="polite" className="mt-2 text-gray-600">
                                No match found. You can enter your address manually.
                            </div>
                        )}
                        {!addressFieldsVisible && (
                            <button
                                type="button"
                                className="btn btn-link text-start mt-1"
                                style={{ padding: "7px 13px" }}
                                onClick={handleManualEntry}
                            >
                                Enter an address manually
                            </button>
                        )}
                    </div>
                </div>
            )}
        </Form.Group>
    );
};

const formatSuggestion = (address: MelissaApiAddress) =>
    [address.addressLine1, address.city, [address.state, address.postalCode].filter(Boolean).join(" ")]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(", ");

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const highlightMatch = (text: string, query: string): React.ReactNode => {
    const trimmed = query.trim();
    if (!trimmed) {
        return text;
    }

    const segments = text.split(new RegExp(`(${escapeRegExp(trimmed)})`, "gi"));
    let offset = 0;
    return segments.map((segment) => {
        const key = `${offset}-${segment}`;
        offset += segment.length;
        const isMatch = segment.toLowerCase() === trimmed.toLowerCase();
        return isMatch ? <strong key={key}>{segment}</strong> : <React.Fragment key={key}>{segment}</React.Fragment>;
    });
};

export default AddressTypeahead;
